import React, { useMemo, useState } from "react";
import { Bot, MessageCircle, Send, Sparkles, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const suggestions = [
  "How do I book a wedding shoot?",
  "Which albums can I view?",
  "What should I wear for a pre-wedding shoot?",
  "How early should we book?",
  "Where can I contact you?"
];

const keywordScore = (text, keywords) =>
  keywords.reduce((score, keyword) => (text.includes(keyword) ? score + 1 : score), 0);

const pickTopAlbums = (albums = []) =>
  albums
    .slice(0, 3)
    .map((album) => `${album.title}${album.location ? `, ${album.location}` : ""}`)
    .join(" | ");

const pickTopVideos = (videos = []) =>
  videos
    .slice(0, 3)
    .map((video) => `${video.title} (${video.type || "Video"})`)
    .join(" | ");

const buildKnowledgeBase = (context) => {
  const albumCount = context.albums?.length || 0;
  const videoCount = context.videos?.length || 0;

  return [
    {
      id: "booking",
      keywords: ["book", "booking", "reserve", "date", "availability", "available", "hire", "appointment"],
      answer:
        "Use the Book Your Event button on the homepage. Share your event date, location, and guest count. For wedding photography, it is better to book as early as possible so the date is still open."
    },
    {
      id: "albums",
      keywords: ["album", "albums", "gallery", "photos", "portfolio", "pictures", "work"],
      answer:
        albumCount > 0
          ? `There are ${albumCount} albums available right now. Start in the Wedding Albums section. Featured albums: ${pickTopAlbums(context.albums)}.`
          : "Open the Wedding Albums section to browse the portfolio."
    },
    {
      id: "videos",
      keywords: ["video", "videos", "film", "films", "trailer", "trailers", "reel", "youtube"],
      answer:
        videoCount > 0
          ? `There are ${videoCount} wedding films or reels listed right now. Start in Cinematic Wedding Films. Featured videos: ${pickTopVideos(context.videos)}.`
          : "Open Cinematic Wedding Films to watch the available wedding trailers and reels."
    },
    {
      id: "contact",
      keywords: ["contact", "call", "phone", "email", "whatsapp", "reach", "address", "location"],
      answer:
        "Use the Contact section on the homepage. You can contact Sagar Sen Photography by email at info.sagarsenphotography@gmail.com or phone at +91 9051353785."
    },
    {
      id: "pricing",
      keywords: ["price", "pricing", "cost", "package", "packages", "budget", "quote", "rate"],
      answer:
        "This website does not publish fixed package pricing. The correct flow is to send the booking details first, then share a quote based on date, location, coverage hours, and deliverables."
    },
    {
      id: "prewedding",
      keywords: ["pre wedding", "pre-wedding", "engagement", "outfit", "wear", "dress", "style", "styling"],
      answer:
        "For a pre-wedding shoot, choose coordinated outfits, avoid busy prints, and pick colors that suit the location. Bring one clean formal look and one relaxed look. If the shoot is outdoors, schedule near sunrise or sunset for better light."
    },
    {
      id: "timeline",
      keywords: ["timeline", "schedule", "time", "when", "golden hour", "sunset", "morning", "best time"],
      answer:
        "The best portraits are usually around sunrise or sunset. For weddings, keep buffer time before key rituals so portraits, family photos, and candid coverage do not feel rushed."
    },
    {
      id: "delivery",
      keywords: ["delivery", "deliver", "editing", "edited", "photoshop", "retouch", "album delivery"],
      answer:
        "Delivery timelines depend on project scope. The practical workflow is to confirm deliverables during booking: edited photos, teaser images, film, album design, and final delivery date."
    },
    {
      id: "travel",
      keywords: ["travel", "destination", "outside city", "outstation", "destination wedding"],
      answer:
        "Yes, destination or outstation shoots can be handled, but travel, stay, and schedule should be confirmed while booking so the quote matches the event plan."
    },
    {
      id: "admin",
      keywords: ["admin", "dashboard", "upload", "homepage", "manage", "delete", "replace"],
      answer:
        "The admin panel lets you manage images, albums, videos, bookings, and homepage selections. If content changes do not persist after refresh, that means Firebase write permissions still need adjustment."
    }
  ];
};

const buildReply = (message, context) => {
  const text = message.toLowerCase().trim();

  if (!text) {
    return "Ask about bookings, albums, videos, pricing, contact details, pre-wedding planning, or photography tips.";
  }

  const knowledgeBase = buildKnowledgeBase(context);
  const ranked = knowledgeBase
    .map((entry) => ({ ...entry, score: keywordScore(text, entry.keywords) }))
    .sort((a, b) => b.score - a.score);

  if (ranked[0]?.score > 0) {
    return ranked[0].answer;
  }

  if (text.includes("who are you") || text.includes("what can you do")) {
    return "I am the site assistant for Sagar Sen Photography. I can help with bookings, albums, videos, contact details, website navigation, and general wedding photography planning questions.";
  }

  if (text.includes("hello") || text.includes("hi") || text.includes("hey")) {
    return "Ask me about booking, albums, videos, pricing flow, contact details, pre-wedding preparation, or photography planning.";
  }

  return "I do not use a paid AI API here, but I can still help with booking flow, albums, videos, contact details, pricing flow, pre-wedding planning, timelines, outfit guidance, and how the website works.";
};

const WebsiteAssistant = ({ albums = [], videos = [] }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "I can help with bookings, albums, videos, contact details, pricing flow, pre-wedding planning, and general photography questions."
    }
  ]);

  const context = useMemo(() => ({ albums, videos }), [albums, videos]);

  const sendMessage = (rawMessage) => {
    const message = rawMessage.trim();
    if (!message) return;

    const reply = buildReply(message, context);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: message },
      { role: "assistant", text: reply }
    ]);
    setInput("");
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {open ? (
        <div className="w-[min(340px,calc(100vw-1rem))] rounded-3xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-gray-900 p-2 text-white">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">AI Photo Concierge</p>
                <p className="text-xs text-gray-500">Local assistant, no paid API</p>
              </div>
            </div>
            <button
              type="button"
              className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-80 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  message.role === "assistant"
                    ? "bg-gray-100 text-gray-800"
                    : "ml-auto bg-gray-900 text-white"
                }`}
              >
                {message.text}
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 px-4 py-3">
            <div className="mb-3 flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  onClick={() => sendMessage(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <form
              className="flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                sendMessage(input);
              }}
            >
              <Input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask about photography or the website"
              />
              <Button type="submit" size="icon">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          className="h-12 max-w-[calc(100vw-1rem)] rounded-full px-4 shadow-xl sm:h-14 sm:px-5"
          onClick={() => setOpen(true)}
        >
          <Sparkles className="mr-2 h-5 w-5" />
          Ask AI Concierge
        </Button>
      )}
    </div>
  );
};

export default WebsiteAssistant;
